"use client"

import { useState, useEffect } from "react"
import { useParams, useSearchParams } from "next/navigation"
import DirectImage from "@/components/ui/direct-image"
import { getProducts, getProductById } from "@/lib/api/dutchie"
import { findProductBySlug, createProductSlug, findSimilarProductBySlug } from "@/lib/slug-utils"
import { useCart } from "@/lib/context/cart-context"
import { formatCategoryName } from "@/lib/format-text"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowLeft, Plus, Minus, Cannabis, Tag } from "lucide-react"
import Link from "next/link"
import TerpeneWheel, { CannabinoidList, parseTerpenesFromDescription } from "@/components/shop/terpene-wheel"
import LowStockBadge, { totalQuantity } from "@/components/shop/low-stock-badge"

export default function ProductPage() {
  const params = useParams()
  const searchParams = useSearchParams()
  const variantId = searchParams.get("variant")
  const { addToCart } = useCart()
  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [quantity, setQuantity] = useState(1)
  const [selectedVariant, setSelectedVariant] = useState<string | null>(variantId)
  const [previousPage, setPreviousPage] = useState<string>('/shop')
  const [relatedProducts, setRelatedProducts] = useState<any[]>([])

  // Immediate scroll to top to prevent flash/blip when navigating from scrolled position
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Use immediate scroll with no animation to prevent flash
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, []); // Run only once on mount for immediate effect

  // Capture the previous page URL when the component mounts
  useEffect(() => {
    // Try to get the referrer from session storage first (in case of page refresh)
    const storedReferrer = typeof window !== 'undefined' ? sessionStorage.getItem('productReferrer') : null;
    
    if (storedReferrer) {
      console.log('Using stored referrer:', storedReferrer);
      setPreviousPage(storedReferrer);
    } else if (typeof document !== 'undefined') {
      // First attempt: check document.referrer
      if (document.referrer) {
        try {
          // If we have a referrer and it's from the same site
          const referrerUrl = new URL(document.referrer);
          const currentUrl = new URL(window.location.href);
          
          if (referrerUrl.origin === currentUrl.origin) {
            const referrerPath = referrerUrl.pathname + referrerUrl.search;
            console.log('Using document.referrer:', referrerPath);
            setPreviousPage(referrerPath);
            // Store in session storage in case of refresh
            sessionStorage.setItem('productReferrer', referrerPath);
            return; // Exit early if we set from document.referrer
          }
        } catch (e) {
          console.error('Error parsing referrer:', e);
        }
      }
      
      // Second attempt: Check if we came from a specific page in history
      if (window.history.state && window.history.state.previousPage) {
        console.log('Using history state:', window.history.state.previousPage);
        setPreviousPage(window.history.state.previousPage);
        sessionStorage.setItem('productReferrer', window.history.state.previousPage);
        return;
      }
      
      // Fallback: Look for common patterns in URL 
      const urlParams = new URLSearchParams(window.location.search);
      const fromParam = urlParams.get('from');
      if (fromParam) {
        console.log('Using from param:', fromParam);
        setPreviousPage(fromParam);
        sessionStorage.setItem('productReferrer', fromParam);
      } else {
        // Set default based on category if we can extract it from the product
        if (product && product.category) {
          const categoryPath = `/shop/category/${product.category.toLowerCase().replace(/ /g, '-')}`;
          console.log('Using category fallback:', categoryPath);
          setPreviousPage(categoryPath);
          sessionStorage.setItem('productReferrer', categoryPath);
        } else {
          // Ultimate fallback
          console.log('Using default fallback: /shop');
          setPreviousPage('/shop');
          sessionStorage.setItem('productReferrer', '/shop');
        }
      }
    }
  }, [product]); // Include product as a dependency so we can use it for the category fallback

  useEffect(() => {
    async function loadProduct() {
      if (typeof params.slug !== "string") return

      try {
        let productData = null;
        
        // First, check if slug is actually a Dutchie product ID
        // This maintains backwards compatibility with the ID-based URLs
        if (params.slug.includes("-") === false && params.slug.length > 20) {
          productData = await getProductById(params.slug, variantId || undefined);
          if (productData) {
            setProduct(productData);
            // Redirect to slug-based URL for better SEO
            const productSlug = createProductSlug(productData);
            const redirectUrl = `/shop/product/${productSlug}${variantId ? `?variant=${variantId}` : ''}`;
            // Use a client-side redirect that won't disrupt the user experience
            if (window.location.pathname !== redirectUrl.split('?')[0]) {
              window.history.replaceState({}, '', redirectUrl);
            }
            return;
          }
        }

        // Look up the product by slug
        const productBySlug = await findProductBySlug(params.slug, getProducts)
        
        if (!productBySlug) {
          // Slug did not resolve. Old Google/PR links point at renamed or
          // delisted products (e.g. "joints" → "pre-rolls"). Try a fuzzy match
          // and redirect if we find a close cousin, otherwise fall through to
          // the not-found render.
          const similar = await findSimilarProductBySlug(params.slug, getProducts);
          if (similar) {
            const targetSlug = createProductSlug(similar);
            console.log(`No exact match for "${params.slug}", redirecting to similar: "${targetSlug}"`);
            window.location.replace(`/shop/product/${targetSlug}`);
            return;
          }
          console.error("Product not found by slug:", params.slug);
          return;
        }
        
        // If variant ID is specified, use it to get the specific variant
        if (variantId) {
          const productWithVariant = await getProductById(productBySlug.id, variantId);
          if (productWithVariant) {
            setProduct(productWithVariant);
            
            // Make sure the URL has the correct slug
            const correctSlug = createProductSlug(productWithVariant);
            if (params.slug !== correctSlug) {
              const redirectUrl = `/shop/product/${correctSlug}?variant=${variantId}`;
              window.history.replaceState({}, '', redirectUrl);
            }
          }
        } else {
          setProduct(productBySlug);
          
          // Make sure the URL has the correct slug
          const correctSlug = createProductSlug(productBySlug);
          if (params.slug !== correctSlug) {
            window.history.replaceState({}, '', `/shop/product/${correctSlug}`);
          }
        }
        
      } catch (error) {
        console.error("Error loading product:", error)
      } finally {
        setLoading(false)
      }
    }

    loadProduct()
  }, [params.slug, variantId])

  // Fetch related products when product loads
  useEffect(() => {
    async function loadRelatedProducts() {
      if (!product) return
      
      try {
        const allProducts = await getProducts()
        
        // Find products in same category, excluding current product
        const related = allProducts
          .filter((p: any) => 
            p.id !== product.id && 
            (p.category === product.category || p.brand === product.brand)
          )
          .slice(0, 8) // Limit to 8 products
        
        setRelatedProducts(related)
      } catch (error) {
        console.error("Error loading related products:", error)
      }
    }
    
    loadRelatedProducts()
  }, [product?.id, product?.category, product?.brand])

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity)
    }
  }

  const incrementQuantity = () => {
    setQuantity(prev => Math.min(prev + 1, 99)) // Cap at 99 items
  }

  const decrementQuantity = () => {
    setQuantity(prev => Math.max(prev - 1, 1)) // Minimum of 1
  }

  const handleVariantChange = (variant: string) => {
    if (product && product.id) {
      // Get the proper slug for the product
      const productSlug = createProductSlug(product);
      
      // Create a URL with the correct slug and new variant
      const newUrl = `/shop/product/${productSlug}?variant=${variant}`;
      
      // Update the URL without causing a full page reload
      window.history.pushState({}, '', newUrl);
      
      // Load the variant data
      const variantProduct = product.variants?.find((v: {id: string, price: number, specialPrice?: number, option?: string}) => v.id === variant);
      if (variantProduct) {
        // Create a copy of the product with the new variant information
        const updatedProduct = {
          ...product,
          variantId: variant,
          price: variantProduct.price,
          specialPrice: variantProduct.specialPrice,
          option: variantProduct.option,
        };
        
        // Update the product state
        setProduct(updatedProduct);
        // Update the variant state
        setSelectedVariant(variant);
      } else {
        // If for some reason we can't find the variant locally, do a full navigation
        window.location.href = newUrl;
      }
    }
  }

  if (loading) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-screen-lg mx-auto flex justify-center items-center min-h-[500px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary_green"></div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-screen-lg mx-auto bg-red-50 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-700 mb-4">Product Not Found</h1>
          <div className="flex flex-col-reverse sm:flex-row items-start gap-6">
            <div className="flex-1 min-w-0">
              <Link 
                href={previousPage} 
                className="inline-flex items-center text-gray-500 hover:text-green-700 mb-4 transition">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back {previousPage.includes('/category/') ? `to ${formatCategoryName(previousPage.split('/').pop() || '')}` : 
                      previousPage === '/shop' ? 'to shop' : 
                      previousPage.includes('/brand/') ? 'to brand page' : 
                      previousPage.includes('/collection/') ? 'to collection' : 
                      'to shop'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if the product has variants
  const variants = product.variants || []

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-screen-lg mx-auto grid md:grid-cols-2 gap-10 lg:gap-14">
        {/* Product Image - smaller on mobile */}
        <div className="relative aspect-[4/3] sm:aspect-square rounded-2xl overflow-hidden bg-white shadow-sm ring-1 ring-gray-100 flex items-center justify-center max-h-[280px] sm:max-h-none">
          <img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder.png';
            }}
          />
          {product.strainType && product.strainType !== "NOT_APPLICABLE" && (
            <div
              className={`absolute top-4 left-4 text-xs font-bold px-3 py-1.5 rounded-full capitalize inline-flex items-center shadow-sm ${(() => {
                const s = product.strainType.toLowerCase()
                if (s.includes("indica")) return "bg-purple-100 text-purple-900 border border-purple-200"
                if (s.includes("sativa")) return "bg-amber-100 text-amber-900 border border-amber-200"
                if (s.includes("hybrid")) return "bg-emerald-100 text-emerald-900 border border-emerald-200"
                if (s.includes("cbd")) return "bg-sky-100 text-sky-900 border border-sky-200"
                return "bg-gray-100 text-gray-800 border border-gray-200"
              })()}`}
            >
              <Cannabis className="w-3.5 h-3.5 mr-1" />
              {product.strainType.toLowerCase()}
            </div>
          )}
          
          {product.specialPrice && product.price > product.specialPrice && (
            <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full flex items-center">
              <Tag className="w-3.5 h-3.5 mr-1" />
              {Math.round(((product.price - product.specialPrice) / product.price) * 100)}% OFF
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex flex-col">
          {/* Breadcrumb navigation */}
          <div className="text-sm breadcrumbs mb-6">
            <Link href={previousPage} className="text-gray-500 hover:text-green-700 transition-colors inline-flex items-center">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {previousPage.includes('/category/') ? `Back to ${formatCategoryName(previousPage.split('/').pop()?.split('?')[0] || '')}` :
               previousPage.includes('/search') ? 'Back to Search Results' :
               previousPage.includes('/brand/') ? `Back to ${previousPage.split('/').pop()?.split('?')[0]?.replace(/-/g, ' ')}` :
               previousPage.includes('/collection/') ? `Back to Collection` :
               previousPage === '/shop/all' ? 'Back to All Products' :
               'Back to Shop'}
            </Link>
          </div>

          {/* Name / Brand */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight text-balance">{product.name}</h1>
          {product.brand && (
            <Link 
              href={`/shop/brand/${encodeURIComponent(typeof product.brand === 'string' ? product.brand : product.brand.name).toLowerCase()}`}
              className="text-sm text-primary_green font-medium hover:underline inline-flex items-center mb-1"
            >
              <Tag className="h-3 w-3 mr-1" />
              {typeof product.brand === 'string' ? product.brand : product.brand.name}
            </Link>
          )}
          
          {/* Price + low-stock urgency chip */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {product.specialPrice ? (
              <>
                <span className="text-3xl font-extrabold text-green-700">${product.specialPrice.toFixed(2)}</span>
                <span className="text-base text-gray-500 line-through">${product.price.toFixed(2)}</span>
              </>
            ) : (
              <span className="text-3xl font-extrabold text-green-700">${product.price.toFixed(2)}</span>
            )}
            {(() => {
              const selVariant = product.variants?.find((v: any) => v.id === selectedVariant);
              const qty = selVariant?.quantity ?? totalQuantity(product);
              return <LowStockBadge quantity={qty} />;
            })()}
          </div>

          <Separator className="my-6" />

          {/* Potency + Effects + Terpenes, the "vibe" strip. */}
          <div className="mt-6 space-y-4">
            {/* Potency pills */}
            {(product.thc || product.cbd) && (
              <div className="flex flex-wrap gap-2">
                {product.thc && (
                  <div className="inline-flex items-center gap-2 bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg px-4 py-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">THC</span>
                    <span className="text-lg font-extrabold tabular-nums text-emerald-900">{product.thc}</span>
                  </div>
                )}
                {product.cbd && (
                  <div className="inline-flex items-center gap-2 bg-gradient-to-br from-sky-50 to-sky-100 border border-sky-200 rounded-lg px-4 py-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-sky-700">CBD</span>
                    <span className="text-lg font-extrabold tabular-nums text-sky-900">{product.cbd}</span>
                  </div>
                )}
              </div>
            )}

            {/* Effects chips, visible above the fold instead of buried in the Effects tab. */}
            {product.effects && product.effects.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Effects</p>
                <div className="flex flex-wrap gap-1.5">
                  {product.effects.slice(0, 8).map((effect: string, i: number) => (
                    <span
                      key={i}
                      className="inline-flex items-center bg-primary_green/10 text-primary_green text-xs font-semibold px-2.5 py-1 rounded-full"
                    >
                      {effect}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Terpene profile — prefers Dutchie structured lab data (real %
                values + proportional donut + ranked bars), falls back to names
                parsed from the description. Cannabinoid breakdown shows below
                when structured data is present. */}
            {(() => {
              const profile = product.terpeneProfile;
              const nameList = (!profile || profile.length === 0)
                ? (product.terpenes && product.terpenes.length > 0
                    ? product.terpenes
                    : parseTerpenesFromDescription(product.description))
                : undefined;
              const showTerps = (profile && profile.length > 0) || (nameList && nameList.length > 0);
              const showCannas = product.cannabinoidProfile && product.cannabinoidProfile.length > 0;
              if (!showTerps && !showCannas) return null;
              return (
                <div className="pt-4 border-t border-gray-100 space-y-6">
                  {showTerps && <TerpeneWheel profile={profile} terpenes={nameList} size={200} />}
                  {showCannas && <CannabinoidList profile={product.cannabinoidProfile} />}
                </div>
              );
            })()}
          </div>

          {/* Variants */}
          {variants.length > 1 && (
            <div className="mt-6">
              <label className="text-sm font-medium text-gray-700 block mb-2">Size / Option</label>
              <div className="flex flex-wrap gap-2">
                {variants.map((variant: any) => (
                  <Button
                    key={variant.id}
                    variant={variant.id === selectedVariant ? "default" : "outline"}
                    className={variant.id === selectedVariant ? "bg-primary_green hover:bg-primary_green/90" : ""}
                    onClick={() => handleVariantChange(variant.id)}
                  >
                    {variant.option}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity & CTA */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto,1fr] md:items-end gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Quantity</label>
              <div className="flex items-center">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-10 w-10 rounded-l-md"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="h-10 w-12 flex items-center justify-center border-y border-gray-200 bg-white">
                  {quantity}
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-10 w-10 rounded-r-md"
                  onClick={incrementQuantity}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              className="md:w-full w-full md:row-span-2 bg-primary_green hover:bg-primary_green/90"
              size="lg"
              onClick={handleAddToCart}
            >
              Add to Cart
            </Button>
          </div>

          {/* Product Description */}
          <div className="mt-8">
            <Tabs defaultValue="description">
              <TabsList className="border-b border-gray-200">
                <TabsTrigger value="description">Description</TabsTrigger>
                <TabsTrigger value="effects">Effects & Uses</TabsTrigger>
              </TabsList>
              <TabsContent value="description" className="mt-4 text-gray-700">
                {product.description ?
                  <p className="text-[15px] leading-relaxed whitespace-pre-line">{product.description}</p> :
                  <p className="text-sm text-gray-500 italic">No description available for this product.</p>
                }
              </TabsContent>
              <TabsContent value="effects" className="mt-4 text-gray-700">
                {product.effects && product.effects.length > 0 ? (
                  <div>
                    <p className="mb-2"><strong>Effects:</strong></p>
                    <div className="flex flex-wrap gap-2">
                      {product.effects.map((effect: string, index: number) => (
                        <span key={index} className="bg-gray-100 px-2.5 py-1 rounded-full text-sm">
                          {effect}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">Effects information not available for this product.</p>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* --- You might also like --- */}
      {relatedProducts.length > 0 && (
        <div className="mt-16 max-w-screen-lg mx-auto">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-primary_green">Recommended</p>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">You might also like</h2>
            </div>
            <span className="text-xs text-gray-500 hidden sm:inline">Swipe for more</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4" style={{scrollbarWidth: 'none', msOverflowStyle: 'none'}}>
            {relatedProducts.map((relatedProduct) => (
              <Link
                key={relatedProduct.id}
                href={`/shop/product/${createProductSlug(relatedProduct)}`}
                className="flex-shrink-0 w-40 sm:w-48 group"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-white shadow-sm ring-1 ring-gray-100 mb-2">
                  <img
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                    onError={(e) => { (e.target as HTMLImageElement).src = '/placeholder.png'; }}
                  />
                </div>
                <p className="text-xs text-green-700 font-medium truncate">{relatedProduct.brand}</p>
                <p className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-green-700 transition-colors">{relatedProduct.name}</p>
                <p className="text-sm font-bold text-green-700 mt-1">${relatedProduct.specialPrice?.toFixed(2) || relatedProduct.price?.toFixed(2)}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
